import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Scissors,
  Clock,
  DollarSign,
  MapPin,
  Star,
  Calendar as CalendarIcon,
  User,
  Mail,
  Phone,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Sparkles,
  Gift,
  Download,
  Home
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { format, addDays, isSameDay, parseISO, setHours, setMinutes } from "date-fns";
import SEOMeta, { generateOGImage } from "@/components/SEOMeta";

const GuestBookingPage = () => {
  const { stylistId } = useParams();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref");
  
  // States
  const [loading, setLoading] = useState(true);
  const [stylist, setStylist] = useState(null);
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1); // 1: Select Service, 2: Select Date/Time, 3: Enter Details, 4: Confirmation
  
  // Booking details
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Guest details
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [notes, setNotes] = useState("");
  
  // Confirmation
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch stylist profile and services
  useEffect(() => {
    fetchStylistData();
  }, [stylistId]);

  const fetchStylistData = async () => {
    setLoading(true);
    try {
      // Fetch stylist profile (includes user info)
      const profileRes = await axios.get(`${API}/stylists/${stylistId}`);
      const data = profileRes.data;
      
      // Merge user info with profile
      const stylistData = {
        ...data,
        name: data.user?.name || data.business_name || "Stylist",
        email: data.user?.email,
        profile_image: data.profile_image || data.user?.profile_image,
        rating: data.rating || 0
      };
      setStylist(stylistData);
      
      // Fetch services - try stylist-specific first, fallback to global
      try {
        const servicesRes = await axios.get(`${API}/services/stylist/${stylistId}`);
        setServices(servicesRes.data || []);
      } catch {
        // Fallback to global services
        const globalServices = await axios.get(`${API}/services/all`);
        setServices(globalServices.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch stylist:", error);
      toast.error("Failed to load stylist information");
    } finally {
      setLoading(false);
    }
  };

  // Fetch available time slots when date is selected
  useEffect(() => {
    if (selectedDate && selectedService) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedService]);

  const fetchAvailableSlots = async () => {
    if (!selectedDate || !selectedService) return;
    
    setLoadingSlots(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await axios.get(`${API}/stylists/${stylistId}/availability`, {
        params: { date: dateStr, duration: selectedService.duration }
      });
      
      // Handle both array of strings and array of objects
      const slots = res.data.slots || [];
      const formattedSlots = slots.map(slot => {
        if (typeof slot === 'string') return slot;
        if (slot.time) return slot.time;
        if (slot.available) return slot.available;
        return String(slot);
      });
      
      setAvailableSlots(formattedSlots);
    } catch (error) {
      console.error("Failed to fetch slots:", error);
      // Generate default slots if API fails
      generateDefaultSlots();
    } finally {
      setLoadingSlots(false);
    }
  };

  const generateDefaultSlots = () => {
    // Generate hourly slots from 9 AM to 6 PM
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < 18) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    setAvailableSlots(slots);
  };

  const handleSubmitBooking = async () => {
    if (!guestName || (!guestEmail && !guestPhone)) {
      toast.error("Please provide your name and either email or phone number");
      return;
    }
    
    setSubmitting(true);
    try {
      const bookingData = {
        stylist_id: stylistId,
        service_id: selectedService.service_id || selectedService.id,
        service_name: selectedService.name,
        service_price: selectedService.price,
        service_duration: selectedService.duration,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        guest_name: guestName,
        guest_email: guestEmail || null,
        guest_phone: guestPhone || null,
        notes: notes,
        referral_code: referralCode || null,
        is_guest_booking: true
      };
      
      const res = await axios.post(`${API}/bookings/guest`, bookingData);
      setBooking(res.data);
      setShowSuccess(true);
      setStep(4);
      toast.success("Appointment booked successfully!");
    } catch (error) {
      console.error("Booking failed:", error);
      toast.error(error.response?.data?.detail || "Failed to book appointment");
    } finally {
      setSubmitting(false);
    }
  };

  // Disable past dates and dates more than 30 days out
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = addDays(today, 30);
    return date < today || date > maxDate;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-stone-400">Loading stylist information...</p>
        </div>
      </div>
    );
  }

  if (!stylist) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950 flex items-center justify-center">
        <Card className="bg-stone-900 border-stone-700 max-w-md">
          <CardContent className="p-8 text-center">
            <Scissors className="w-16 h-16 text-stone-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Stylist Not Found</h2>
            <p className="text-stone-400 mb-6">The stylist you&apos;re looking for doesn&apos;t exist or is no longer available.</p>
            <Link to="/">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
      {/* Dynamic SEO Meta Tags for Social Sharing */}
      {stylist && (
        <SEOMeta
          title={`Book with ${stylist.name} on MyHairChair`}
          description={`Book your next appointment with ${stylist.name}${stylist.city ? ` in ${stylist.city}` : ''}. Skip the fees, pay direct via Zelle, PayPal, or Cash App.`}
          image={generateOGImage(stylist)}
          stylistName={stylist.name}
          shopName={stylist.business_name || stylist.city}
          profileImage={stylist.profile_image || stylist.user?.profile_picture}
        />
      )}
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-stone-900/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/images/myhairchair-logo.png" 
              alt="MyHairChair" 
              className="h-10 sm:h-12 md:h-14 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link 
              to="/"
              className="flex items-center gap-1 text-stone-400 hover:text-white text-sm transition-colors"
              data-testid="back-to-app-btn"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Back to App</span>
            </Link>
            <Badge className="bg-emerald-500/20 text-emerald-400">Guest Booking</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Marketing Banner - Quick Booking Benefits */}
        {step === 1 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Book in 60 Seconds — No Account Needed!</h3>
                <p className="text-stone-400 text-sm">
                  Just pick your service, choose a time, and enter your contact info. 
                  <span className="text-amber-400"> Want rewards? </span> 
                  <Link to="/register" className="text-amber-400 underline hover:text-amber-300">
                    Create a free account
                  </Link>
                  {" "}to earn points on every visit!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s 
                  ? 'bg-amber-500 text-black' 
                  : 'bg-stone-800 text-stone-500'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-16 h-1 mx-2 ${
                  step > s ? 'bg-amber-500' : 'bg-stone-800'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Stylist Info Card */}
        <Card className="bg-stone-900/50 border-stone-700 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              {stylist.profile_image ? (
                <img 
                  src={stylist.profile_image} 
                  alt={stylist.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-amber-500"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                  <Scissors className="w-8 h-8 text-black" />
                </div>
              )}
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">{stylist.name}</h2>
                <p className="text-amber-400 text-sm">{stylist.professional_type || "Professional Stylist"}</p>
                <div className="flex items-center gap-3 mt-1 text-sm text-stone-400">
                  {stylist.rating > 0 && (
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      {stylist.rating.toFixed(1)}
                    </span>
                  )}
                  {stylist.city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {stylist.city}, {stylist.state}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Scissors className="w-5 h-5 text-amber-400" />
                Select a Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              {services.length === 0 ? (
                <p className="text-stone-400 text-center py-8">No services available at this time.</p>
              ) : (
                <div className="grid gap-3">
                  {services.map((service) => (
                    <button
                      key={service.service_id || service.id}
                      onClick={() => {
                        setSelectedService(service);
                        setStep(2);
                      }}
                      className={`w-full p-4 rounded-xl border transition-all text-left ${
                        selectedService?.id === service.id
                          ? 'border-amber-500 bg-amber-500/10'
                          : 'border-stone-700 hover:border-stone-600 bg-stone-800/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-semibold">{service.name}</h3>
                          {service.description && (
                            <p className="text-stone-400 text-sm mt-1">{service.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-amber-400 font-bold">${service.price}</span>
                            <span className="text-stone-500 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {service.duration} min
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-stone-500" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && (
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-amber-400" />
                Choose Date & Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div>
                  <Label className="text-stone-300 mb-2 block">Select Date</Label>
                  <div className="bg-stone-800 rounded-xl p-4">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setSelectedTime(null);
                      }}
                      disabled={isDateDisabled}
                      className="text-white"
                    />
                  </div>
                </div>

                {/* Time Slots */}
                <div>
                  <Label className="text-stone-300 mb-2 block">Select Time</Label>
                  {!selectedDate ? (
                    <div className="bg-stone-800 rounded-xl p-8 text-center">
                      <CalendarIcon className="w-12 h-12 text-stone-600 mx-auto mb-2" />
                      <p className="text-stone-500">Please select a date first</p>
                    </div>
                  ) : loadingSlots ? (
                    <div className="bg-stone-800 rounded-xl p-8 text-center">
                      <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-2" />
                      <p className="text-stone-500">Loading available times...</p>
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="bg-stone-800 rounded-xl p-8 text-center">
                      <Clock className="w-12 h-12 text-stone-600 mx-auto mb-2" />
                      <p className="text-stone-500">No available times for this date</p>
                    </div>
                  ) : (
                    <div className="bg-stone-800 rounded-xl p-4 max-h-[300px] overflow-y-auto">
                      <div className="grid grid-cols-3 gap-2">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            onClick={() => setSelectedTime(slot)}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              selectedTime === slot
                                ? 'bg-amber-500 text-black'
                                : 'bg-stone-700 text-white hover:bg-stone-600'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Service Summary */}
              <div className="mt-6 p-4 bg-stone-800/50 rounded-xl">
                <p className="text-stone-400 text-sm">Selected Service:</p>
                <p className="text-white font-semibold">{selectedService?.name} - ${selectedService?.price}</p>
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="border-stone-600 text-white hover:bg-stone-800"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={!selectedDate || !selectedTime}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Enter Details */}
        {step === 3 && (
          <Card className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                Your Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-stone-300">Full Name *</Label>
                  <Input
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Enter your full name"
                    className="bg-stone-800 border-stone-700 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-stone-300">Email Address</Label>
                  <Input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="bg-stone-800 border-stone-700 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-stone-300">Phone Number</Label>
                  <Input
                    type="tel"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-stone-800 border-stone-700 text-white mt-1"
                  />
                </div>

                <p className="text-stone-500 text-sm">* Email or phone required for appointment confirmation</p>

                {/* Registration Nudge */}
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-emerald-400 text-sm flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    <span>
                      <strong>Tip:</strong> Create a free account to earn loyalty points & unlock exclusive rewards!
                    </span>
                  </p>
                </div>

                <div>
                  <Label className="text-stone-300">Notes (Optional)</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special requests or notes..."
                    className="bg-stone-800 border-stone-700 text-white mt-1"
                  />
                </div>

                {/* Booking Summary */}
                <div className="mt-6 p-4 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-xl border border-amber-500/30">
                  <h3 className="text-white font-semibold mb-3">Booking Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone-400">Service:</span>
                      <span className="text-white">{selectedService?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-400">Date:</span>
                      <span className="text-white">{selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-400">Time:</span>
                      <span className="text-white">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-400">Duration:</span>
                      <span className="text-white">{selectedService?.duration} minutes</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-stone-700">
                      <span className="text-stone-300 font-semibold">Total:</span>
                      <span className="text-amber-400 font-bold text-lg">${selectedService?.price}</span>
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-6">
                  <Button 
                    variant="outline" 
                    onClick={() => setStep(2)}
                    className="border-stone-600 text-white hover:bg-stone-800"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleSubmitBooking}
                    disabled={!guestName || (!guestEmail && !guestPhone) || submitting}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        Confirm Booking
                        <CheckCircle className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <Card className="bg-stone-900 border-stone-700">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h2>
              <p className="text-stone-400 mb-6">Your appointment has been booked successfully.</p>

              <div className="bg-stone-800 rounded-xl p-6 text-left mb-6">
                <h3 className="text-white font-semibold mb-4">Appointment Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <Scissors className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-stone-400">Service</p>
                      <p className="text-white">{selectedService?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-stone-400">Stylist</p>
                      <p className="text-white">{stylist?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-stone-400">Date & Time</p>
                      <p className="text-white">{selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")} at {selectedTime}</p>
                    </div>
                  </div>
                  {stylist?.address && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-amber-400" />
                      <div>
                        <p className="text-stone-400">Location</p>
                        <p className="text-white">{stylist.address}, {stylist.city}, {stylist.state}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-stone-400 text-sm mb-6">
                {guestEmail && `A confirmation has been sent to ${guestEmail}.`}
                {guestPhone && !guestEmail && `You'll receive a text confirmation at ${guestPhone}.`}
              </p>

              {/* Add to Calendar Button - Pro Feature showcase */}
              {booking?.appointment_id && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs">
                      <Sparkles className="w-3 h-3 mr-1" />
                      PRO FEATURE
                    </Badge>
                  </div>
                  <a 
                    href={`${API}/appointments/${booking.appointment_id}/calendar`}
                    download={`appointment_${booking.appointment_id}.ics`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-stone-800 hover:bg-stone-700 text-white rounded-lg transition-colors border border-stone-600"
                  >
                    <Download className="w-5 h-5" />
                    Add to Calendar
                  </a>
                  <p className="text-stone-500 text-xs mt-2">Download .ics file for Google Calendar, Outlook, or Apple Calendar</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/">
                  <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                    Back to Home
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Account & Earn Rewards
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default GuestBookingPage;
